import type { ReactNode } from 'react'
import { useCatalogo, usePaesi, useSessione } from '../servizi/sessione.tsx'
import { Campo, Scelta, Spunta } from '../componenti/base.tsx'
import { Dato } from '../componenti/Pezzi.tsx'
import {
  DatoDerivato, GruppoCaselle, SceltaComune, SceltaEnte, SceltaMultipla, SceltaSedeAppartenenza,
  useLingue, vociDi,
} from '../componenti/persona-campi.tsx'
import { codiceDaVoce, opzioniConAltreVoci } from '../servizi/lingue.ts'
import {
  MASSIMO_LINGUE_VEICOLARI, NOMI_DATI_IDENTIFICATIVI, comuneVuoto, giornoIso, haAttributo, oggiLocale, tipoOperatore,
  voceEsclusiva,
  type CataloghiRegole, type Errori, type LuogoLetto, type ModoNascita, type SchedaLetta, type StatoAbitativa,
  type StatoAnagrafica, type StatoCondizione, type StatoFormazione, type StatoServizi, type TipoPersona,
} from '../servizi/regole-anagrafica.ts'
import { opzioniSedi } from '../servizi/sedi.ts'
import { mancaObbligatorio } from '../servizi/dati-mancanti.ts'

/**
 * Sezioni della Scheda utente Rev05 nell'ordine del modulo: come e' venuto a conoscenza del
 * Servizio, dati anagrafici, condizione giuridica, formazione – lavoro, condizione abitativa e
 * familiare. Ogni sezione ha i campi per la creazione e la modifica e la lettura per la scheda.
 *
 * I campi che dipendono da un'altra scelta (motivo, settore, testo di «altro», comune o localita'
 * di nascita) compaiono quando la scelta li ammette, e cambiando la scelta si svuotano. Restano
 * visibili se contengono gia' un valore non ammesso, per esempio un dato del vecchio archivio:
 * il modulo lo segnala accanto al campo e l'utente lo corregge, invece di perderlo senza vederlo.
 */

type Cambia<T> = (parziale: Partial<T>) => void

/** Nota dei campi riempiti con «Compila dal documento» (lettura con l'AI), al posto dell'aiuto. */
export const NOTA_LETTO = 'Letto dal documento: controlla.'

/**
 * Campi riempiti con «Compila dal documento»: evidenziati, con la nota, finche' l'operatore non li
 * tocca. Senza `letti` (la modifica della scheda) i campi restano come sono. Con `letti`, anche
 * vuoto, ogni campo ha sempre il suo involucro: se comparisse solo quando il campo e' evidenziato,
 * al primo tasto React ricreerebbe il campo e il fuoco andrebbe perso.
 */
function campiLetti(letti: ReadonlySet<string> | undefined) {
  const letto = (campo: string) => !!letti?.has(campo)
  const evidenzia = (campo: string, campoFiglio: ReactNode) => (letti
    ? <div className={`campo-lettura${letto(campo) ? ' campo-letto' : ''}`}>{campoFiglio}</div>
    : campoFiglio)
  return { letto, evidenzia }
}

/** Cataloghi della sessione con gli attributi delle voci, e le opzioni di una tendina. */
export function useRegole() {
  const { cataloghi } = useSessione()
  const cat = (cataloghi ?? {}) as CataloghiRegole
  const opzioni = (nome: string) => vociDi(cataloghi, nome).map((v) => ({ valore: v.codice, etichetta: v.nome }))
  const ha = (catalogo: string, codice: string, attributo: 'richiede_specifica' | 'ammette_motivo' | 'ammette_settore') =>
    haAttributo(cat, catalogo, codice, attributo)
  return { cataloghi, cat, opzioni, ha }
}

/** Data AAAA-MM-GG in lettura come GG/MM/AAAA, senza passare dal fuso del dispositivo. */
export const giornoLeggibile = (valore: unknown): string | null => {
  const giorno = giornoIso(valore)
  return giorno ? giorno.split('-').reverse().join('/') : null
}

const conTesto = (nome: string | null | undefined, testo: unknown) =>
  nome ? `${nome}${testo ? `: ${String(testo)}` : ''}` : null

/** Riconoscimento del titolo di studio in Italia (453). */
const RICONOSCIMENTI = [
  { valore: 'si', etichetta: 'Sì' },
  { valore: 'no', etichetta: 'No' },
  { valore: 'in_corso', etichetta: 'In corso di riconoscimento' },
]

/** Etichette del tipo di persona se il catalogo non e' ancora arrivato (D-07). */
const TIPI_RISERVA = [
  { valore: 'assistito', etichetta: 'Migrante o con background migratorio' },
  { valore: 'operatore_ente', etichetta: 'Operatore' },
  { valore: 'operatore_cidis', etichetta: 'CIDIS Dipendenti e Collaboratori' },
]

/** Aiuto del profilo per i tipi con le regole dell'operatore (D-07, 481). */
const AIUTO_TIPO: Partial<Record<TipoPersona, string>> = {
  operatore_ente: 'Chi rappresenta un ente: serve l’ente di appartenenza; l’ambito professionale è facoltativo.',
  operatore_cidis: 'Personale di CIDIS, per la formazione annuale del Bilancio sociale: serve la sede CIDIS di '
    + 'appartenenza (oppure l’ente), come per l’operatore; l’ambito professionale è facoltativo.',
}

// --- intestazione ----------------------------------------------------------------------------

export function CampoTipoPersona({ valore, cambia, errore }: {
  valore: TipoPersona; cambia: (v: TipoPersona) => void; errore?: string
}) {
  const { opzioni } = useRegole()
  const voci = opzioni('tipo_persona')
  return (
    <Scelta etichetta="Profilo utenti" obbligatorio valore={valore} errore={errore}
            cambia={(v) => cambia(v as TipoPersona)} opzioni={voci.length ? voci : TIPI_RISERVA}
            aiuto={AIUTO_TIPO[valore]} />
  )
}

export function CampoSede({ valore, cambia, errore, obbligatorio }: {
  valore: string; cambia: (v: string) => void; errore?: string; obbligatorio: boolean
}) {
  const { sedi } = useSessione()
  return (
    <Scelta etichetta="Sede Servizio" obbligatorio={obbligatorio} valore={valore} cambia={cambia}
            errore={errore} vuota="— seleziona —"
            opzioni={opzioniSedi(sedi)}
            aiuto="Sede di riferimento della persona: decide chi vede la scheda." />
  )
}

// --- come e' venuto a conoscenza del Servizio (D-15) ------------------------------------------

export function CampiConoscenza({ stato: s, cambia, errori: e }: {
  stato: StatoAnagrafica; cambia: Cambia<StatoAnagrafica>; errori: Errori
}) {
  const { cataloghi, ha } = useRegole()
  const conTestoAltro = (codici: string[]) => codici.some((c) => ha('canale_conoscenza', c, 'richiede_specifica'))
  return (
    <>
      <GruppoCaselle
        legenda="Come è venuto a conoscenza del Servizio" voci={vociDi(cataloghi, 'canale_conoscenza')}
        scelte={s.canali_conoscenza} errore={e.canali_conoscenza} aiuto="Una o più voci."
        cambia={(codici) => cambia({ canali_conoscenza: codici, ...(conTestoAltro(codici) ? {} : { canale_specifica: '' }) })}
      />
      {conTestoAltro(s.canali_conoscenza) && (
        <div className="griglia-modulo">
          <Campo etichetta="Altro: specificare" valore={s.canale_specifica} maxLength={300}
                 cambia={(v) => cambia({ canale_specifica: v })} aiuto="Facoltativo." />
        </div>
      )}
    </>
  )
}

export function LetturaConoscenza({ scheda: s }: { scheda: SchedaLetta }) {
  if (!s.canali_conoscenza) return <p className="tenue piccolo">Non visibile sulla scheda mascherata.</p>
  if (!s.canali_conoscenza.length) return <p className="tenue piccolo">Non indicato.</p>
  return (
    <ul className="persona-voce-elenco">
      {s.canali_conoscenza.map((c) => <li key={c.codice}>{conTesto(c.nome, c.specifica)}</li>)}
    </ul>
  )
}

// --- dati anagrafici (D-07, D-11...D-14) -----------------------------------------------------

const MODI_NASCITA = [
  { valore: 'data', etichetta: 'data completa' },
  { valore: 'anno', etichetta: 'solo l’anno' },
  { valore: 'ignota', etichetta: 'non nota' },
]

/**
 * «Dati anonimizzati» (481): per chi non puo' firmare l'informativa (irregolari, minori, persone
 * protette). Attivandola i dati identificativi si svuotano e la data completa diventa l'anno;
 * togliendola nome e cognome si riscrivono per intero e, senza codice fiscale, si sceglie il motivo.
 */
function CampoAnonimizzati({ stato: s, cambia, errore, ostacoli, registrata }: {
  stato: StatoAnagrafica; cambia: Cambia<StatoAnagrafica>; errore?: string; ostacoli: string[]; registrata: boolean
}) {
  const anonima = s.dati_anonimizzati
  const bloccata = !anonima && ostacoli.length > 0
  const scegli = (v: boolean) => cambia(v
    ? {
        dati_anonimizzati: true, cf: '', motivo_assenza_cf_codice: '', motivo_assenza_cf: '', telefono: '', email: '',
        residenza_indirizzo: '', domicilio_indirizzo: '', comune_nascita: comuneVuoto(), luogo_nascita_estero: '',
        ...(s.modo_nascita === 'data'
          ? { modo_nascita: 'anno' as ModoNascita, anno_nascita: s.data_nascita.slice(0, 4), data_nascita: '' } : {}),
      }
    : { dati_anonimizzati: false, nome: '', cognome: '', motivo_assenza_cf_codice: '', motivo_assenza_cf: '' })
  const aiuto = bloccata
    ? `Si attiva solo su una scheda senza dati identificativi. Da togliere prima: ${
      ostacoli.map((o) => NOMI_DATI_IDENTIFICATIVI[o] ?? o).join(', ')}.`
    : anonima && registrata
      ? 'Per toglierla scrivi nome e cognome per intero: da lì valgono le regole normali, consensi compresi.'
      : 'Per chi non può firmare l’informativa (irregolari, minori, persone protette): solo le iniziali, niente '
        + 'recapiti, codice fiscale, indirizzi, documenti né data di nascita completa. Gli interventi si registrano '
        + 'senza i consensi.'
  return (
    <>
      <Spunta etichetta="Dati anonimizzati" valore={anonima} cambia={scegli} disabilitato={bloccata} aiuto={aiuto} />
      {errore && <p className="campo-errore" role="alert">{errore}</p>}
    </>
  )
}

export function CampiDatiAnagrafici({
  stato: s, cambia, errori: e, conTipo = false, vedeIdentificativi = true, anonimizzazione, letti,
}: {
  stato: StatoAnagrafica; cambia: Cambia<StatoAnagrafica>; errori: Errori; conTipo?: boolean
  /** Falso in modifica senza `persona.identificativi`: il codice fiscale arriva mascherato. */
  vedeIdentificativi?: boolean
  /**
   * «Dati anonimizzati» (481): `ostacoli` sono i dati identificativi registrati che impediscono di
   * attivarla (nessuno alla creazione), `registrata` dice se la scheda e' gia' anonimizzata.
   */
  anonimizzazione?: { ostacoli: string[]; registrata: boolean }
  /** Nuova scheda: i campi riempiti con «Compila dal documento» e non ancora toccati. */
  letti?: ReadonlySet<string>
}) {
  const { opzioni, ha } = useRegole()
  const { letto, evidenzia } = campiLetti(letti)
  const { paesi } = usePaesi()
  const lingue = useLingue()
  const oggi = oggiLocale()
  const italia = s.paese_nascita === 'ITA'
  const estero = !!s.paese_nascita && !italia
  const operatore = tipoOperatore(s.tipo)
  const anonima = s.dati_anonimizzati
  const modiNascita = anonima ? MODI_NASCITA.filter((m) => m.valore !== 'data') : MODI_NASCITA
  const opzioniPaesi = paesi.map((p) => ({ valore: p.codice_iso3, etichetta: p.nome }))
  const ambitoConTesto = ha('ambito_professionale', s.ambito_professionale, 'richiede_specifica')
  // Il codice fiscale mascherato non e' un valore da correggere: modificato partirebbe con gli
  // asterischi (400 CF_NON_VALIDO), svuotato cancellerebbe il codice reale mai visto. Resta in
  // sola lettura e fuori dalla PATCH, che manda solo i campi cambiati.
  const cfMascherato = !vedeIdentificativi && s.cf.trim() !== ''

  return (
    <>
      {conTipo && (
        <div className="griglia-modulo">
          <CampoTipoPersona valore={s.tipo} cambia={(tipo) => cambia({ tipo })} errore={e.tipo} />
        </div>
      )}

      <fieldset className="persona-sottosezione">
        <legend>Persona</legend>
        {(s.tipo === 'assistito' || anonima) && (
          <CampoAnonimizzati stato={s} cambia={cambia} errore={e.dati_anonimizzati}
                             ostacoli={anonimizzazione?.ostacoli ?? []} registrata={!!anonimizzazione?.registrata} />
        )}
        <div className="griglia-modulo">
          {evidenzia('cognome', (
            <Campo etichetta={anonima ? 'Iniziali del cognome' : 'Cognome'} obbligatorio valore={s.cognome}
                   maxLength={anonima ? 12 : 100} autoComplete="off"
                   cambia={(v) => cambia({ cognome: v })} errore={e.cognome}
                   aiuto={anonima ? 'Da una a tre lettere, anche con il punto: «M.», «M. A.», «MA».'
                     : letto('cognome') ? NOTA_LETTO : undefined} />
          ))}
          {evidenzia('nome', (
            <Campo etichetta={anonima ? 'Iniziali del nome' : 'Nome'} obbligatorio valore={s.nome}
                   maxLength={anonima ? 12 : 100} autoComplete="off"
                   cambia={(v) => cambia({ nome: v })} errore={e.nome}
                   aiuto={letto('nome') ? NOTA_LETTO : undefined} />
          ))}
          {evidenzia('genere_codice', (
            <Scelta etichetta="Genere" valore={s.genere_codice} vuota="— non indicato —" opzioni={opzioni('genere')}
                    cambia={(v) => cambia({ genere_codice: v })} errore={e.genere_codice}
                    aiuto={letto('genere_codice') ? NOTA_LETTO : undefined} />
          ))}
          {evidenzia('paese_nascita', (
            <Scelta etichetta="Paese d’origine" valore={s.paese_nascita} vuota="— seleziona —" opzioni={opzioniPaesi}
                    errore={e.paese_nascita} aiuto={letto('paese_nascita') ? NOTA_LETTO : undefined}
                    cambia={(v) => cambia({
                      paese_nascita: v,
                      // D-12: comune italiano oppure localita' estera, mai entrambi.
                      ...(v === 'ITA' ? { luogo_nascita_estero: '' } : { comune_nascita: comuneVuoto() }),
                      ...(v ? {} : { luogo_nascita_estero: '' }),
                    })} />
          ))}
          {/* Indicazioni CIDIS del 17/10/2026: i due campi restano sempre al loro posto, e quello
              che non vale per il paese scelto diventa grigio invece di sparire. Un valore storico
              non ammesso (D-12) lascia il campo attivo, perche' si possa togliere. Con i dati
              anonimizzati (481) il luogo di nascita non si registra. */}
          {!anonima && (
            <>
              {evidenzia('comune_nascita', (
                <SceltaComune etichetta="Comune di nascita (Italia)" valore={s.comune_nascita}
                              disabilitato={!italia && s.comune_nascita.testo === ''}
                              cambia={(v) => cambia({ comune_nascita: v })} errore={e.comune_nascita}
                              aiuto={letto('comune_nascita') ? NOTA_LETTO
                                : italia ? undefined : 'Solo per chi è nato in Italia.'} />
              ))}
              {evidenzia('luogo_nascita_estero', (
                <Campo etichetta="Località di nascita (estero)" valore={s.luogo_nascita_estero} maxLength={200}
                       disabilitato={!estero && s.luogo_nascita_estero === ''}
                       cambia={(v) => cambia({ luogo_nascita_estero: v })} errore={e.luogo_nascita_estero}
                       aiuto={letto('luogo_nascita_estero') ? NOTA_LETTO
                         : estero ? 'Facoltativa: città o località.' : 'Solo per chi è nato all’estero.'} />
              ))}
            </>
          )}
          <Scelta etichetta="Precisione della data di nascita" valore={s.modo_nascita} opzioni={modiNascita}
                  cambia={(v) => cambia({ modo_nascita: v as ModoNascita })} errore={e.modo_nascita} />
          {s.modo_nascita === 'data' && evidenzia('data_nascita', (
            <Campo tipo="date" etichetta="Data di nascita" obbligatorio valore={s.data_nascita} max={oggi}
                   cambia={(v) => cambia({ data_nascita: v })} errore={e.data_nascita}
                   aiuto={letto('data_nascita') ? NOTA_LETTO : undefined} />
          ))}
          {s.modo_nascita === 'anno' && (
            <Campo tipo="number" etichetta="Anno di nascita" obbligatorio valore={s.anno_nascita}
                   min={1900} max={Number(oggi.slice(0, 4))} passi={1} modalitaTastiera="numeric"
                   cambia={(v) => cambia({ anno_nascita: v })} errore={e.anno_nascita} />
          )}
          {s.modo_nascita === 'ignota' && (
            <Scelta etichetta="Fascia d’età" valore={s.fascia_eta_manuale} vuota="— non indicata —"
                    opzioni={opzioni('fascia_eta')} cambia={(v) => cambia({ fascia_eta_manuale: v })}
                    errore={e.fascia_eta_manuale}
                    aiuto="Senza data di nascita si sceglie a mano: serve per chiudere gli interventi, e finché manca la scheda resta da completare." />
          )}
          {/* 460: la data del vecchio archivio da verificare diventa esatta solo con la conferma. */}
          {s.precisione_registrata === 'da_verificare' && s.modo_nascita !== 'anno' && (
            <Spunta valore={s.nascita_verificata} cambia={(v) => cambia({ nascita_verificata: v })}
                    etichetta={s.modo_nascita === 'data'
                      ? 'Ho controllato la data su un documento'
                      : 'Ho controllato: la data di nascita non è nota'}
                    aiuto="La data viene dal vecchio archivio ed è da verificare. Con la spunta non lo è più." />
          )}
          {evidenzia('cittadinanza', (
            <Scelta etichetta="Cittadinanza" valore={s.cittadinanza} vuota="— seleziona —" opzioni={opzioniPaesi}
                    cambia={(v) => cambia({ cittadinanza: v })} errore={e.cittadinanza}
                    aiuto={letto('cittadinanza') ? NOTA_LETTO : undefined} />
          ))}
        </div>
      </fieldset>

      <fieldset className="persona-sottosezione">
        <legend>Residenza e domicilio</legend>
        <div className="griglia-modulo">
          <SceltaComune etichetta="Comune di residenza" valore={s.residenza_comune}
                        cambia={(v) => cambia({ residenza_comune: v })} errore={e.residenza_comune} />
          <DatoDerivato etichetta="Provincia" valore={s.residenza_comune.provincia}
                        aiuto="Ricavata dal comune." />
          {!anonima && (
            <Campo etichetta="Indirizzo di residenza" valore={s.residenza_indirizzo} maxLength={300}
                   cambia={(v) => cambia({ residenza_indirizzo: v })} errore={e.residenza_indirizzo} />
          )}
        </div>
        <Spunta etichetta="Domicilio diverso dalla residenza" valore={s.domicilio_diverso}
                cambia={(v) => cambia({ domicilio_diverso: v })} />
        {s.domicilio_diverso && (
          <div className="griglia-modulo">
            <SceltaComune etichetta="Comune di domicilio" valore={s.domicilio_comune}
                          cambia={(v) => cambia({ domicilio_comune: v })} errore={e.domicilio_comune} />
            <DatoDerivato etichetta="Provincia" valore={s.domicilio_comune.provincia} aiuto="Ricavata dal comune." />
            {!anonima && (
              <Campo etichetta="Indirizzo di domicilio" valore={s.domicilio_indirizzo} maxLength={300}
                     cambia={(v) => cambia({ domicilio_indirizzo: v })} errore={e.domicilio_indirizzo} />
            )}
          </div>
        )}
      </fieldset>

      {anonima ? (
        <p className="persona-nota-sezione">
          Dati anonimizzati: niente recapiti, codice fiscale e indirizzi. Il motivo dell’assenza del codice fiscale è
          «Dati anonimizzati».
        </p>
      ) : (
      <fieldset className="persona-sottosezione">
        <legend>Recapiti e codice fiscale</legend>
        <div className="griglia-modulo">
          <Campo tipo="tel" etichetta="Recapito telefonico" valore={s.telefono} maxLength={40}
                 modalitaTastiera="tel" cambia={(v) => cambia({ telefono: v })} errore={e.telefono} />
          <Campo tipo="email" etichetta="E-mail" valore={s.email} modalitaTastiera="email"
                 cambia={(v) => cambia({ email: v })} errore={e.email} />
          {cfMascherato ? (
            <DatoDerivato etichetta="Codice fiscale" valore={<span className="mono">{s.cf}</span>}
                          aiuto="Si vede per intero e si modifica con il permesso sugli identificativi." />
          ) : evidenzia('cf', (
            <div className="campo-maiuscolo">
              <Campo etichetta="Codice fiscale" valore={s.cf} autoComplete="off" maxLength={30}
                     cambia={(v) => cambia({ cf: v })} errore={e.cf}
                     aiuto={letto('cf') ? NOTA_LETTO
                       : 'Anche il codice numerico provvisorio, che è di 11 cifre. STP, CUI e gli altri codici vanno in «Altri codici».'} />
            </div>
          ))}
          {!s.cf.trim() && (
            <Scelta etichetta="Perché non ha il codice fiscale" obbligatorio vuota="— scegli —"
                    valore={s.motivo_assenza_cf_codice} opzioni={opzioni('motivo_assenza_cf')}
                    cambia={(v) => cambia({ motivo_assenza_cf_codice: v })} errore={e.motivo_assenza_cf_codice} />
          )}
          {!s.cf.trim() && ha('motivo_assenza_cf', s.motivo_assenza_cf_codice, 'richiede_specifica') && (
            <Campo etichetta="In attesa di che cosa" obbligatorio valore={s.motivo_assenza_cf} maxLength={200}
                   cambia={(v) => cambia({ motivo_assenza_cf: v })} errore={e.motivo_assenza_cf} />
          )}
        </div>
      </fieldset>
      )}

      <fieldset className="persona-sottosezione">
        <legend>Lingue</legend>
        <div className="griglia-modulo">
          <Scelta etichetta="Lingua madre" valore={s.lingua_madre} vuota="— non indicata —"
                  opzioni={opzioniConAltreVoci(lingue)}
                  cambia={(v) => cambia({ lingua_madre: codiceDaVoce(v) })} errore={e.lingua_madre} />
          <SceltaMultipla etichetta="Altre lingue parlate" voci={lingue} scelte={s.lingue_veicolari}
                          vuota="— scegliere una lingua —" errore={e.lingue_veicolari}
                          aiuto={`Una o più, al massimo ${MASSIMO_LINGUE_VEICOLARI}.`}
                          massimo={MASSIMO_LINGUE_VEICOLARI}
                          cambia={(codici) => cambia({ lingue_veicolari: codici })} />
          {/* Dal 17/10/2026 un dato della persona, obbligatorio, qui e non piu' in Formazione. */}
          {!operatore && (
            <Scelta etichetta="Conoscenza della lingua italiana" obbligatorio vuota="— scegli —"
                    valore={s.livello_italiano} opzioni={opzioni('livello_italiano')}
                    cambia={(v) => cambia({ livello_italiano: v })} errore={e.livello_italiano} />
          )}
        </div>
      </fieldset>

      {operatore && (
        <fieldset className="persona-sottosezione">
          <legend>Ente</legend>
          <div className="griglia-modulo">
            <SceltaEnte etichetta="Ente di appartenenza" obbligatorio valore={s.ente_appartenenza}
                        disabilitato={!!s.sede_appartenenza}
                        cambia={(v) => cambia({ ente_appartenenza: v })} errore={e.ente_appartenenza_id} />
            {/* N-2: le sedi CIDIS non sono piu' registrate come enti. L'operatore appartiene a una
                sede oppure a un ente esterno, mai a entrambi: ciascuna scelta disabilita l'altra. */}
            <SceltaSedeAppartenenza valore={s.sede_appartenenza} errore={e.sede_appartenenza}
                                    disabilitato={!!s.ente_appartenenza.id}
                                    cambia={(v) => cambia({ sede_appartenenza: v })} />
            <DatoDerivato etichetta="Tipologia ente" valore={s.ente_appartenenza.tipologia}
                          aiuto="Ricavata dalla categoria dell’ente di appartenenza." />
            <SceltaEnte etichetta="Ente presso cui si opera" valore={s.ente_operativo} aiuto="Facoltativo."
                        cambia={(v) => cambia({ ente_operativo: v })} errore={e.ente_operativo_id} />
            <Scelta etichetta="Ambito professionale" valore={s.ambito_professionale} vuota="— non indicato —"
                    opzioni={opzioni('ambito_professionale')} errore={e.ambito_professionale}
                    cambia={(v) => cambia({
                      ambito_professionale: v,
                      ...(ha('ambito_professionale', v, 'richiede_specifica') ? {} : { ambito_professionale_specifica: '' }),
                    })} />
            {(ambitoConTesto || s.ambito_professionale_specifica !== '') && (
              <Campo etichetta="Ambito professionale: altro" valore={s.ambito_professionale_specifica} maxLength={300}
                     cambia={(v) => cambia({ ambito_professionale_specifica: v })}
                     errore={e.ambito_professionale_specifica} aiuto="Facoltativo." />
            )}
          </div>
        </fieldset>
      )}
    </>
  )
}

const provincia = (luogo: LuogoLetto | null | undefined) =>
  luogo?.provincia_nome ? `${luogo.provincia_nome}${luogo.provincia_sigla ? ` (${luogo.provincia_sigla})` : ''}` : null

export function LetturaDatiAnagrafici({ scheda: s }: { scheda: SchedaLetta }) {
  const genere = useCatalogo('genere')
  const fasce = useCatalogo('fascia_eta')
  const paesi = usePaesi()
  const nascita = !s.data_nascita
    ? (s.precisione_nascita === 'da_verificare' ? 'non nota, da verificare' : 'non nota')
    : s.mascherata
      ? String(s.data_nascita)
      : s.precisione_nascita === 'solo_anno'
        ? `${giornoIso(s.data_nascita).slice(0, 4)} (solo l’anno)`
        : `${giornoLeggibile(s.data_nascita)}${s.precisione_nascita === 'stimata' ? ' (stimata)'
          : s.precisione_nascita === 'da_verificare' ? ' (da verificare)' : ''}`
  const op = s.operatore
  // Osservazioni 4 (23/10/2026): i dati obbligatori che mancano si vedono dove stanno, in tono di
  // attenzione. Quali siano lo dice l'API (dati_mancanti), gia' ridotto a cio' che chi guarda puo' sapere.
  const manca = (...codici: string[]) => mancaObbligatorio(s.dati_mancanti, ...codici)

  return (
    <>
      <p className="persona-sottotitolo">Persona</p>
      <dl className="persona-dati">
        <Dato nome="Profilo utenti">{s.tipo_nome}</Dato>
        {s.dati_anonimizzati && (
          <Dato nome="Dati anonimizzati">sì: nome e cognome sono iniziali, nessun dato identificativo</Dato>
        )}
        <Dato nome="Cognome" mancante={manca('cognome')}>{s.cognome ?? null}</Dato>
        <Dato nome="Nome" mancante={manca('nome')}>{s.nome ?? null}</Dato>
        <Dato nome="Genere">{genere.nomeDi(s.genere_codice) || null}</Dato>
        <Dato nome="Paese d’origine">{paesi.nomeDi(s.paese_nascita) || null}</Dato>
        {!s.mascherata && (
          <Dato nome="Luogo di nascita">{s.comune_nascita_nome ?? s.luogo_nascita_estero ?? null}</Dato>
        )}
        <Dato nome="Data di nascita" mancante={manca('nascita')}>{nascita}</Dato>
        {!s.mascherata && (
          <Dato nome="Fascia d’età" mancante={manca('fascia_eta')}>
            {s.fascia_eta ? `${fasce.nomeDi(s.fascia_eta)}${s.fascia_eta_manuale ? ' (indicata a mano)' : ''}` : null}
          </Dato>
        )}
        <Dato nome="Cittadinanza">{paesi.nomeDi(s.cittadinanza) || null}</Dato>
      </dl>

      {s.mascherata ? (
        <p className="tenue piccolo">
          Residenza, recapiti, lingue ed enti non sono visibili sulla scheda mascherata.
        </p>
      ) : (
        <>
          <p className="persona-sottotitolo">Residenza e domicilio</p>
          <dl className="persona-dati">
            <Dato nome="Comune di residenza">{s.residenza?.nome ?? null}</Dato>
            <Dato nome="Provincia di residenza">{provincia(s.residenza)}</Dato>
            <Dato nome="Indirizzo di residenza">{s.residenza_indirizzo || null}</Dato>
            {s.domicilio_diverso && (
              <>
                <Dato nome="Comune di domicilio">{s.domicilio?.nome ?? null}</Dato>
                <Dato nome="Provincia di domicilio">{provincia(s.domicilio)}</Dato>
                <Dato nome="Indirizzo di domicilio">{s.domicilio_indirizzo || null}</Dato>
              </>
            )}
          </dl>

          <p className="persona-sottotitolo">Recapiti e codice fiscale</p>
          <dl className="persona-dati">
            <Dato nome="Recapito telefonico">{s.telefono ? <span className="mono">{s.telefono}</span> : null}</Dato>
            <Dato nome="E-mail">{s.email || null}</Dato>
            <Dato nome="Codice fiscale" mancante={manca('codice_fiscale')}>
              {s.cf ? <span className="mono">{s.cf}</span>
                : s.motivo_assenza_cf_nome
                  ? <span className="tenue">non c’è: {s.motivo_assenza_cf_codice === 'ATTESA_ALTRO'
                      ? `in attesa di ${s.motivo_assenza_cf ?? ''}` : s.motivo_assenza_cf_nome.toLowerCase()}</span>
                  : s.motivo_assenza_cf ? <span className="tenue">non c’è: {s.motivo_assenza_cf}</span> : null}
            </Dato>
          </dl>

          <p className="persona-sottotitolo">Lingue</p>
          <dl className="persona-dati">
            <Dato nome="Lingua madre">{s.lingua_madre_nome ?? null}</Dato>
            <Dato nome="Altre lingue parlate">
              {s.lingue_veicolari?.length ? s.lingue_veicolari.map((l) => l.nome).join(', ') : null}
            </Dato>
            {!tipoOperatore(s.tipo) && (
              <Dato nome="Conoscenza della lingua italiana" mancante={manca('livello_italiano')}>
                {s.livello_italiano_nome ?? null}
              </Dato>
            )}
          </dl>

          {tipoOperatore(s.tipo) && op && (
            <>
              <p className="persona-sottotitolo">Ente</p>
              <dl className="persona-dati">
                <Dato nome="Ente di appartenenza" mancante={manca('appartenenza')}>
                  {op.ente_appartenenza?.nome ?? null}
                </Dato>
                <Dato nome="Sede CIDIS di appartenenza" mancante={manca('appartenenza')}>
                  {op.sede_appartenenza?.nome ?? null}
                </Dato>
                <Dato nome="Tipologia ente">{op.tipologia_ente?.nome ?? null}</Dato>
                <Dato nome="Ente presso cui si opera">{op.ente_operativo?.nome ?? null}</Dato>
                <Dato nome="Ambito professionale">
                  {conTesto(op.ambito_professionale?.nome, op.ambito_professionale_specifica)}
                </Dato>
              </dl>
            </>
          )}
        </>
      )}
    </>
  )
}

// --- condizione giuridica (D-16) -------------------------------------------------------------

export function CampiCondizione({ stato: s, cambia, errori: e, anonimizzati = false, letti }: {
  stato: StatoCondizione; cambia: Cambia<StatoCondizione>; errori: Errori
  /** Scheda con dati anonimizzati (481): la scadenza del titolo di soggiorno non si registra. */
  anonimizzati?: boolean
  /** Nuova scheda: i campi riempiti con «Compila dal documento» e non ancora toccati. */
  letti?: ReadonlySet<string>
}) {
  const { opzioni, ha } = useRegole()
  const { letto, evidenzia } = campiLetti(letti)
  const ammette = ha('condizione_giuridica', s.condizione_giuridica, 'ammette_motivo')
  return (
    <div className="griglia-modulo">
      {evidenzia('condizione_giuridica', (
        <Scelta etichetta="Condizione giuridica" valore={s.condizione_giuridica} vuota="— non indicata —"
                opzioni={opzioni('condizione_giuridica')} errore={e.condizione_giuridica}
                aiuto={letto('condizione_giuridica') ? NOTA_LETTO : undefined}
                cambia={(v) => cambia({
                  condizione_giuridica: v,
                  ...(ha('condizione_giuridica', v, 'ammette_motivo') ? {} : { motivo_permesso: '' }),
                })} />
      ))}
      {(ammette || s.motivo_permesso !== '') && evidenzia('motivo_permesso', (
        <Scelta etichetta="Motivo" valore={s.motivo_permesso} vuota="— non indicato —"
                opzioni={opzioni('motivo_permesso')} cambia={(v) => cambia({ motivo_permesso: v })}
                errore={e.motivo_permesso} aiuto={letto('motivo_permesso') ? NOTA_LETTO : 'Facoltativo.'} />
      ))}
      {(ammette || s.permesso_scadenza !== '') && (!anonimizzati || s.permesso_scadenza !== '') && evidenzia('permesso_scadenza', (
        <Campo tipo="date" etichetta="Scadenza del titolo di soggiorno" valore={s.permesso_scadenza}
               cambia={(v) => cambia({ permesso_scadenza: v })} errore={e.permesso_scadenza}
               aiuto={letto('permesso_scadenza') ? NOTA_LETTO : 'Facoltativa.'} />
      ))}
    </div>
  )
}

export function LetturaCondizione({ scheda: s }: { scheda: SchedaLetta }) {
  const condizioni = useCatalogo('condizione_giuridica')
  const motivi = useCatalogo('motivo_permesso')
  if (s.condizione_giuridica === undefined) {
    return <p className="tenue piccolo">Visibile con il permesso di lettura dei dati particolari.</p>
  }
  return (
    <dl className="persona-dati">
      <Dato nome="Condizione giuridica">{condizioni.nomeDi(s.condizione_giuridica) || null}</Dato>
      <Dato nome="Motivo">{motivi.nomeDi(s.motivo_permesso) || null}</Dato>
      <Dato nome="Scadenza del titolo di soggiorno">{giornoLeggibile(s.permesso_scadenza)}</Dato>
    </dl>
  )
}

// --- formazione – lavoro e servizi bancari (D-17, D-18) --------------------------------------

export function CampiFormazione({
  stato: s, cambia, errori: e, servizi, cambiaServizi, erroriServizi,
}: {
  stato: StatoFormazione; cambia: Cambia<StatoFormazione>; errori: Errori
  servizi: StatoServizi; cambiaServizi: Cambia<StatoServizi>; erroriServizi: Errori
}) {
  const { opzioni, ha } = useRegole()
  const situazioneConTesto = ha('situazione_lavorativa', s.situazione, 'richiede_specifica')
  const conSettore = ha('situazione_lavorativa', s.situazione, 'ammette_settore')
  const settoreConTesto = ha('settore_lavorativo', s.settore, 'richiede_specifica')
  const origineConTesto = ha('lavoro_paese_origine', s.lavoro_paese_origine, 'richiede_specifica')

  return (
    <>
      <div className="griglia-modulo">
        <Campo tipo="number" etichetta="Numero anni di studio (conclusi)" valore={s.anni_studio} min={0} max={40}
               passi={1} modalitaTastiera="numeric" cambia={(v) => cambia({ anni_studio: v })} errore={e.anni_studio} />
        <Scelta etichetta="Titolo di studio conseguito" valore={s.titolo_studio} vuota="— non indicato —"
                opzioni={opzioni('titolo_studio')} cambia={(v) => cambia({ titolo_studio: v })} errore={e.titolo_studio} />
        {s.titolo_studio && s.titolo_studio !== 'NESSUNO' && (
          <Scelta etichetta="Il titolo è riconosciuto in Italia?" valore={s.riconoscimento_titolo} vuota="— non indicato —"
                  opzioni={RICONOSCIMENTI} errore={e.riconoscimento_titolo}
                  cambia={(v) => cambia({ riconoscimento_titolo: v as StatoFormazione['riconoscimento_titolo'] })} />
        )}
      </div>

      <fieldset className="persona-sottosezione">
        <legend>Lavoro</legend>
        <div className="griglia-modulo">
          <Scelta etichetta="Situazione lavorativa attuale" valore={s.situazione} vuota="— non indicata —"
                  opzioni={opzioni('situazione_lavorativa')} errore={e.situazione}
                  cambia={(v) => cambia({
                    situazione: v,
                    ...(ha('situazione_lavorativa', v, 'richiede_specifica') ? {} : { situazione_specifica: '' }),
                    // D-17: il settore vale solo per occupato/a e lavoratore autonomo.
                    ...(ha('situazione_lavorativa', v, 'ammette_settore') ? {} : { settore: '', settore_specifica: '' }),
                  })} />
          {(situazioneConTesto || s.situazione_specifica !== '') && (
            <Campo etichetta="Situazione lavorativa: specificare" valore={s.situazione_specifica} maxLength={300}
                   cambia={(v) => cambia({ situazione_specifica: v })} errore={e.situazione_specifica}
                   aiuto="Facoltativo." />
          )}
          {(conSettore || s.settore !== '') && (
            <Scelta etichetta="Settore lavorativo" valore={s.settore} vuota="— non indicato —"
                    opzioni={opzioni('settore_lavorativo')} errore={e.settore}
                    aiuto="Solo per occupato/a e lavoratore autonomo."
                    cambia={(v) => cambia({
                      settore: v, ...(ha('settore_lavorativo', v, 'richiede_specifica') ? {} : { settore_specifica: '' }),
                    })} />
          )}
          {(settoreConTesto || s.settore_specifica !== '') && (
            <Campo etichetta="Settore lavorativo: altro" valore={s.settore_specifica} maxLength={300}
                   cambia={(v) => cambia({ settore_specifica: v })} errore={e.settore_specifica} aiuto="Facoltativo." />
          )}
          <Scelta etichetta="Eventuale lavoro svolto nel Paese di origine" valore={s.lavoro_paese_origine}
                  vuota="— non indicato —" opzioni={opzioni('lavoro_paese_origine')} errore={e.lavoro_paese_origine}
                  cambia={(v) => cambia({
                    lavoro_paese_origine: v,
                    ...(ha('lavoro_paese_origine', v, 'richiede_specifica') ? {} : { lavoro_paese_origine_specifica: '' }),
                  })} />
          {(origineConTesto || s.lavoro_paese_origine_specifica !== '') && (
            <Campo etichetta="Lavoro nel Paese di origine: specificare" valore={s.lavoro_paese_origine_specifica}
                   maxLength={300} cambia={(v) => cambia({ lavoro_paese_origine_specifica: v })}
                   errore={e.lavoro_paese_origine_specifica} aiuto="Facoltativo." />
          )}
        </div>
      </fieldset>

      <CampiServizi servizi={servizi} cambiaServizi={cambiaServizi} erroriServizi={erroriServizi} />
    </>
  )
}

/**
 * Servizi bancari e finanziari (D-18): nel modulo stanno dentro Formazione – Lavoro, e «Completa la
 * scheda» (460) li propone anche da soli.
 */
export function CampiServizi({ servizi, cambiaServizi, erroriServizi }: {
  servizi: StatoServizi; cambiaServizi: Cambia<StatoServizi>; erroriServizi: Errori
}) {
  const { cataloghi, cat, opzioni, ha } = useRegole()
  const esclusiva = voceEsclusiva(servizi, cat)
  const esclusivaDi = (codice: string) => haAttributo(cat, 'strumento_finanziario', codice, 'esclusiva')
  const motivoConTesto = ha('motivo_nessun_servizio_bancario', servizi.motivo, 'richiede_specifica')
  return (
    <fieldset className="persona-sottosezione">
      <legend>Servizi bancari e finanziari</legend>
      <GruppoCaselle
        legenda="Quali dei seguenti servizi bancari/finanziari ha oppure ha già utilizzato in Italia?"
        voci={vociDi(cataloghi, 'strumento_finanziario')} scelte={servizi.voci} errore={erroriServizi.voci}
        inColonna
        aiuto="«NESSUNO di questi servizi» esclude le altre voci."
        disabilitata={(codice) => !!esclusiva && codice !== esclusiva}
        cambia={(codici) => {
          // D-18: spuntare NESSUNO toglie le altre voci; toglierlo toglie anche il motivo.
          const aggiunta = codici.find((c) => !servizi.voci.includes(c))
          if (aggiunta && esclusivaDi(aggiunta)) { cambiaServizi({ voci: [aggiunta] }); return }
          cambiaServizi({ voci: codici, ...(codici.some(esclusivaDi) ? {} : { motivo: '', motivo_specifica: '' }) })
        }}
      />
      {(esclusiva || servizi.motivo !== '') && (
        <div className="griglia-modulo">
          <Scelta etichetta="Se ha risposto NESSUNO, per quale ragione?" valore={servizi.motivo}
                  vuota="— non indicata —" opzioni={opzioni('motivo_nessun_servizio_bancario')}
                  errore={erroriServizi.motivo}
                  cambia={(v) => cambiaServizi({
                    motivo: v,
                    ...(ha('motivo_nessun_servizio_bancario', v, 'richiede_specifica') ? {} : { motivo_specifica: '' }),
                  })} />
          {(motivoConTesto || servizi.motivo_specifica !== '') && (
            <Campo etichetta="Altro motivo" valore={servizi.motivo_specifica} maxLength={300}
                   cambia={(v) => cambiaServizi({ motivo_specifica: v })} errore={erroriServizi.motivo_specifica}
                   aiuto="Facoltativo." />
          )}
        </div>
      )}
    </fieldset>
  )
}

export function LetturaFormazione({ scheda: s }: { scheda: SchedaLetta }) {
  const titoli = useCatalogo('titolo_studio')
  const situazioni = useCatalogo('situazione_lavorativa')
  const settori = useCatalogo('settore_lavorativo')
  const lavori = useCatalogo('lavoro_paese_origine')
  if (s.formazione_lavoro === undefined) return <p className="tenue piccolo">Non visibile con gli incarichi in corso.</p>
  const l = s.formazione_lavoro
  const codice = (campo: string) => (l?.[campo] as string | null | undefined) ?? null
  const sb = s.servizi_bancari
  return (
    <>
      {l ? (
        <>
          <p className="persona-nota-sezione">Ultima rilevazione del {giornoLeggibile(l.rilevata_il)}.</p>
          <dl className="persona-dati">
            <Dato nome="Numero anni di studio (conclusi)">{(l.anni_studio as number | null) ?? null}</Dato>
            <Dato nome="Titolo di studio conseguito">{titoli.nomeDi(codice('titolo_studio')) || null}</Dato>
            {codice('titolo_studio') && codice('titolo_studio') !== 'NESSUNO' && (
              <Dato nome="Titolo riconosciuto in Italia">
                {RICONOSCIMENTI.find((r) => r.valore === l.riconoscimento_titolo)?.etichetta ?? null}
              </Dato>
            )}
            <Dato nome="Situazione lavorativa attuale">
              {conTesto(situazioni.nomeDi(codice('situazione')), l.situazione_specifica)}
            </Dato>
            <Dato nome="Settore lavorativo">{conTesto(settori.nomeDi(codice('settore')), l.settore_specifica)}</Dato>
            <Dato nome="Lavoro svolto nel Paese di origine">
              {conTesto(lavori.nomeDi(codice('lavoro_paese_origine')), l.lavoro_paese_origine_specifica)}
            </Dato>
          </dl>
        </>
      ) : <p className="tenue piccolo">Nessuna rilevazione.</p>}

      <p className="persona-sottotitolo">Servizi bancari e finanziari utilizzati in Italia</p>
      {sb?.voci.length
        ? <ul className="persona-voce-elenco">{sb.voci.map((v) => <li key={v.codice}>{v.nome}</li>)}</ul>
        : <p className="tenue piccolo">Nessuna voce indicata.</p>}
      {sb?.motivo && (
        <dl className="persona-dati">
          <Dato nome="Motivo">{conTesto(sb.motivo.nome, sb.motivo_specifica)}</Dato>
        </dl>
      )}
    </>
  )
}

// --- condizione abitativa e familiare (D-19) -------------------------------------------------

export function CampiAbitativa({ stato: s, cambia, errori: e }: {
  stato: StatoAbitativa; cambia: Cambia<StatoAbitativa>; errori: Errori
}) {
  const { opzioni, ha } = useRegole()
  const conTestoAltro = ha('nucleo_familiare', s.nucleo_familiare, 'richiede_specifica')
  const numero = (campo: keyof StatoAbitativa, etichetta: string, aiuto?: string) => (
    <Campo tipo="number" etichetta={etichetta} valore={s[campo]} min={0} max={50} passi={1}
           modalitaTastiera="numeric" cambia={(v) => cambia({ [campo]: v })} errore={e[campo]} aiuto={aiuto} />
  )
  return (
    <>
      <div className="griglia-modulo">
        <Scelta etichetta="Stato civile" valore={s.stato_civile} vuota="— non indicato —" opzioni={opzioni('stato_civile')}
                cambia={(v) => cambia({ stato_civile: v })} errore={e.stato_civile} />
        <Scelta etichetta="Tipo abitazione" valore={s.soluzione_abitativa} vuota="— non indicato —"
                opzioni={opzioni('soluzione_abitativa')} cambia={(v) => cambia({ soluzione_abitativa: v })}
                errore={e.soluzione_abitativa} />
        <Scelta etichetta="Con chi vive" valore={s.nucleo_familiare} vuota="— non indicato —"
                opzioni={opzioni('nucleo_familiare')} errore={e.nucleo_familiare}
                cambia={(v) => cambia({
                  nucleo_familiare: v,
                  // D-19: chi vive solo/a non ha persone conviventi.
                  ...(v === 'SOLO_A' ? { persone_conviventi: '0' } : {}),
                  ...(ha('nucleo_familiare', v, 'richiede_specifica') ? {} : { nucleo_familiare_specifica: '' }),
                })} />
        {(conTestoAltro || s.nucleo_familiare_specifica !== '') && (
          <Campo etichetta="Con chi vive: altro" valore={s.nucleo_familiare_specifica} maxLength={300}
                 cambia={(v) => cambia({ nucleo_familiare_specifica: v })} errore={e.nucleo_familiare_specifica}
                 aiuto="Facoltativo." />
        )}
        {numero('persone_conviventi', 'Persone conviventi (numero totale)',
          'Chi vive con l’intestatario, escluso l’intestatario; con «solo/a» è zero.')}
      </div>

      <fieldset className="persona-sottosezione">
        <legend>Figli in Italia</legend>
        <div className="griglia-modulo">
          {numero('figli_italia_totale', 'Numero di figli in Italia', 'Non inferiore alla somma delle due fasce.')}
          {numero('figli_italia_minorenni', 'di cui minori di 18 anni')}
          {numero('figli_italia_maggiorenni', 'di cui da 18 anni in su')}
        </div>
      </fieldset>
      <fieldset className="persona-sottosezione">
        <legend>Figli in altri paesi (in patria o all’estero)</legend>
        <div className="griglia-modulo">
          {numero('figli_estero_totale', 'Numero di figli in altri paesi', 'Non inferiore alla somma delle due fasce.')}
          {numero('figli_estero_minorenni', 'di cui minori di 18 anni')}
          {numero('figli_estero_maggiorenni', 'di cui da 18 anni in su')}
        </div>
      </fieldset>
    </>
  )
}

export function LetturaAbitativa({ scheda: s }: { scheda: SchedaLetta }) {
  const stati = useCatalogo('stato_civile')
  const abitazioni = useCatalogo('soluzione_abitativa')
  const nuclei = useCatalogo('nucleo_familiare')
  if (s.condizione_abitativa === undefined) return <p className="tenue piccolo">Non visibile con gli incarichi in corso.</p>
  const a = s.condizione_abitativa
  if (!a) return <p className="tenue piccolo">Nessuna rilevazione.</p>
  const codice = (campo: string) => (a[campo] as string | null | undefined) ?? null
  const numero = (campo: string) => (a[campo] === null || a[campo] === undefined ? null : Number(a[campo]))
  const figli = (dove: 'italia' | 'estero') => {
    const [totale, minori, maggiori] = [numero(`figli_${dove}_totale`), numero(`figli_${dove}_minorenni`),
      numero(`figli_${dove}_maggiorenni`)]
    if (totale === null && minori === null && maggiori === null) return null
    return `${totale ?? '—'} (minori di 18 anni: ${minori ?? '—'}; da 18 anni in su: ${maggiori ?? '—'})`
  }
  return (
    <>
      <p className="persona-nota-sezione">Ultima rilevazione del {giornoLeggibile(a.rilevata_il)}.</p>
      <dl className="persona-dati">
        <Dato nome="Stato civile">{stati.nomeDi(codice('stato_civile')) || null}</Dato>
        <Dato nome="Figli in Italia">{figli('italia')}</Dato>
        <Dato nome="Figli in altri paesi">{figli('estero')}</Dato>
        <Dato nome="Tipo abitazione">{abitazioni.nomeDi(codice('soluzione_abitativa')) || null}</Dato>
        <Dato nome="Con chi vive">{conTesto(nuclei.nomeDi(codice('nucleo_familiare')), a.nucleo_familiare_specifica)}</Dato>
        <Dato nome="Persone conviventi">{numero('persone_conviventi')}</Dato>
      </dl>
    </>
  )
}
